import {
  type ChangeEventHandler,
  type MouseEventHandler,
  useCallback,
  useEffect,
  useState,
} from 'react'

import type { FanOriginDto } from '@api'
import { useUnit } from 'effector-react'

import CloseIcon from '@/assets/icons/icon-close.svg'
import { editFanCategoryFx } from '@/dialogs/EditFanOriginDialog/models.ts'
import { openDialog } from '@/models/dialogs.ts'
import DeleteIcon from '@/modules/chatting/icons/delete.svg'
import EditIcon from '@/modules/chatting/icons/edit.svg'
import CheckIcon from '@/modules/chatting/icons/small-check.svg'
import type { FanOriginCategory } from '@/modules/chatting/types'
import { IconButton } from '@/modules/chatting/ui/components/icon-button'
import { classNames } from '@/utils/helpers'

import { useKeyupHandler } from '../../utils/useKeyupHandler.ts'
import { DIALOG_TYPE } from '../constants'

interface Props {
  category: Required<FanOriginDto>
  isSelected: boolean
  onSelect: (id: FanOriginCategory['id']) => void
}

export const CategoryListItem = ({ category, isSelected, onSelect }: Props) => {
  const isEditing = useUnit(editFanCategoryFx.pending)

  const { id, name, editable } = category
  const [mode, setMode] = useState('label')
  const [value, setValue] = useState(name)

  useEffect(() => {
    setValue(name)
  }, [name])

  const handleInputChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    setValue(event.target.value)
  }

  const handleClickEdit: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.stopPropagation()
    setMode('input')
  }

  const handleClickRemove: React.MouseEventHandler<HTMLButtonElement> = (
    event,
  ) => {
    event.stopPropagation()
    openDialog({ type: DIALOG_TYPE.DELETE_FAN_ORIGIN, origin: category })
  }

  const resetChanges = useCallback(() => {
    setValue(name)
    setMode('label')
  }, [name])

  const handleClickSaveChanges = useCallback(async () => {
    await editFanCategoryFx({ id, name: value })
    resetChanges()
  }, [id, value, resetChanges])

  const handleKeyup = useCallback(
    (event: KeyboardEvent) => {
      if (event.keyCode === 13) {
        handleClickSaveChanges()
      }
    },
    [handleClickSaveChanges],
  )

  useKeyupHandler(handleKeyup, mode === 'input')

  const handleClick = () => {
    if (mode === 'input') return

    onSelect(id)
  }

  const label = (
    <>
      <span>{name}</span>
      {editable && (
        <div className="flex items-center gap-3">
          <IconButton
            icon={EditIcon}
            onClick={handleClickEdit}
            className={classNames('text-primary-500 hover:text-primary-600', {
              'hover:text-primary-300 text-white': isSelected,
            })}
          />
          <IconButton
            icon={DeleteIcon}
            onClick={handleClickRemove}
            className="text-danger-default hover:text-danger-dark"
          />
        </div>
      )}
    </>
  )

  const input = (
    <>
      <input
        autoFocus
        className="border-secondary-100 flex-1 border-b bg-transparent font-medium outline-none placeholder:text-sm placeholder:font-medium placeholder:!leading-5"
        value={value}
        onChange={handleInputChange}
        disabled={isEditing}
        max={50}
      />
      <IconButton
        disabled={isEditing}
        onClick={resetChanges}
        className={classNames('text-secondary-300', {
          'hover:text-primary-300 text-white': isSelected,
        })}
        icon={CloseIcon}
      />
      <IconButton
        disabled={isEditing || !value}
        onClick={handleClickSaveChanges}
        className={classNames('text-primary-500 hover:text-primary-600', {
          'hover:text-primary-300 text-white': isSelected,
        })}
        icon={CheckIcon}
      />
    </>
  )

  return (
    <div
      key={id}
      onClick={handleClick}
      className={classNames(
        'flex max-w-full flex-1 cursor-pointer select-none items-center justify-between gap-2 overflow-hidden text-ellipsis rounded-md px-3 py-1.5 text-sm outline-none',
        {
          'hover:bg-secondary-50 hover:dark:bg-secondary-600': !isSelected,
          'bg-primary-500 text-white': isSelected,
        },
      )}
    >
      {mode === 'label' ? label : input}
    </div>
  )
}
